import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { clearTokens, getAccessToken, getRefreshToken, getTokenExpiry, isTokenExpired, onTokenChange, refreshAccessToken, setTokens } from '../utils/authTokens';
import { clearSession, consumeAuthError } from '../utils/authSession';
import { socketService } from '../services/socket';

interface User {
  id: string;
  email: string;
  name: string;
  role: 'ADMIN' | 'SUPERVISOR' | 'AGENT';
  tenantId?: string;
  tenant?: {
    id: string;
    name?: string;
    domain?: string;
    subdomain?: string;
    isActive?: boolean;
  };
  avatar?: string;
  isActive?: boolean;
  createdAt?: string;
  lastLoginAt?: string;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const refreshTimeoutRef = useRef<number | null>(null);

  const applyAuthData = (data: { token: string; refreshToken?: string; user?: User }) => {
    if (data.token) {
      setTokens(data.token, data.refreshToken);
    }
    if (data.user) {
      setUser(data.user);
      localStorage.setItem('user', JSON.stringify(data.user));
    }
  };

  const triggerRefresh = useCallback(async () => {
    const refreshedToken = await refreshAccessToken();
    if (!refreshedToken) {
      clearSession('Your session expired. Please sign in again.');
    }
  }, []);

  const scheduleTokenRefresh = useCallback((token: string | null) => {
    if (refreshTimeoutRef.current) {
      window.clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }

    if (!token || !getRefreshToken()) {
      return;
    }

    const expiresAt = getTokenExpiry(token);
    if (!expiresAt) {
      return;
    }

    const refreshAt = expiresAt * 1000 - 60 * 1000;
    const delay = refreshAt - Date.now();
    if (delay <= 0) {
      triggerRefresh();
      return;
    }

    refreshTimeoutRef.current = window.setTimeout(() => {
      triggerRefresh();
    }, delay);
  }, [triggerRefresh]);

  const login = async (email: string, password: string) => {
    try {
      setLoading(true);
      setError(null);

      // Construct API URL
      const API_BASE_URL = window.location.hostname === 'localhost'
        ? 'http://172.235.245.13/api'
        : '/api';

      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          applyAuthData({
            token: data.token,
            refreshToken: data.refreshToken,
            user: data.user
          });
          return;
        }
      }

      throw new Error('Invalid credentials');
    } catch (err) {
      console.error('Login error:', err);
      setError(err instanceof Error ? err.message : 'Login failed');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    setError(null);
    clearTokens();
    localStorage.removeItem('user');
    socketService.disconnect();
  };

  // Check for existing session on mount
  useEffect(() => {
    const authError = consumeAuthError();
    if (authError) {
      setError(authError);
    }

    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (err) {
        localStorage.removeItem('user');
      }
    }

    const accessToken = getAccessToken();
    const refreshToken = getRefreshToken();
    if (accessToken && !isTokenExpired(accessToken)) {
      return;
    }

    if (!refreshToken) {
      if (accessToken && isTokenExpired(accessToken)) {
        clearSession('Your session expired. Please sign in again.');
      }
      return;
    }

    refreshAccessToken().then((token) => {
      if (!token) {
        clearSession('Your session expired. Please sign in again.');
      }
    });
  }, []);

  useEffect(() => {
    const unsubscribe = onTokenChange((token) => {
      if (token) {
        const savedUser = localStorage.getItem('user');
        if (savedUser) {
          try {
            setUser(JSON.parse(savedUser));
          } catch (err) {
            localStorage.removeItem('user');
          }
        }
        socketService.updateAuthToken(token);
      } else {
        setUser(null);
        socketService.disconnect();
      }
      scheduleTokenRefresh(token);
    });

    scheduleTokenRefresh(getAccessToken());

    return () => {
      unsubscribe();
      if (refreshTimeoutRef.current) {
        window.clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, [scheduleTokenRefresh]);

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, error }}>
      {children}
    </AuthContext.Provider>
  );
};
